import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal900: '#0d3d3d', teal800: '#0f5454', teal700: '#126868',
  teal600: '#148080', teal500: '#19a8a8', teal400: '#2ecece',
  teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  green50: '#f0fdf4', green100: '#dcfce7', green600: '#16a34a',
  amber50: '#fffbeb', amber100: '#fef3c7', amber500: '#f59e0b', amber600: '#d97706',
  blue50: '#eff6ff', blue100: '#dbeafe', blue600: '#2563eb',
  pink50: '#fdf2f8', pink100: '#fce7f3', pink500: '#ec4899', pink600: '#db2777',
  purple50: '#faf5ff', purple100: '#f3e8ff', purple600: '#9333ea',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

// ─── Default message templates ─────────────────────────────────────────────────
// {Clinic} and {ClinicPhone} are filled from the clinics table
const DEFAULT_TEMPLATES = {
  consultation:
    'Hello {Name}, this is a reminder from {Clinic} for your consultation tomorrow at {Time}. Please arrive 10 minutes early. For queries call us at {ClinicPhone}. - Team {Clinic} 🌿',
  followup:
    'Hello {Name}, your follow-up at {Clinic} is tomorrow at {Time}. Please bring your previous prescription. - Team {Clinic} 🌿',
  treatment:
    'Hello {Name}, your {Service} appointment at {Clinic} is confirmed for tomorrow at {Time}. Please avoid makeup on the treatment area. - Team {Clinic} 🌿',
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Use local date parts — toISOString() gives UTC which can be wrong day in IST/other timezones
function localISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function tomorrowISO() {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return localISO(d)
}
function todayISO() { return localISO(new Date()) }

function fmtTime(t) {
  if (!t) return '—'
  const [h, m] = t.split(':')
  const hr = parseInt(h, 10)
  return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`
}
function fmtDateLong(iso) {
  if (!iso) return '—'
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
}
function fmtDateShort(iso) {
  if (!iso) return '—'
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short',
  })
}
function pickTemplate(service, templates) {
  const s = (service || '').toLowerCase()
  if (s.includes('follow')) return templates.followup
  if (s.includes('consult')) return templates.consultation
  return templates.treatment
}
function buildMessage(appt, templates, clinic) {
  const name    = appt.patients?.full_name || 'Patient'
  const time    = fmtTime(appt.time)
  const service = appt.service_type || 'appointment'
  return pickTemplate(service, templates)
    .replace(/\{Name\}/g, name)
    .replace(/\{Time\}/g, time)
    .replace(/\{Service\}/g, service)
    .replace(/\{Clinic\}/g, clinic?.name || 'the clinic')
    .replace(/\{ClinicPhone\}/g, clinic?.phone || '')
}

// ─── Avatar helpers ─────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  ['#0f5454','#2ecece'], ['#1e3a5f','#60a5fa'], ['#3b1f6e','#a78bfa'],
  ['#5c1a1a','#f87171'], ['#1a4731','#34d399'], ['#5c3d0e','#fbbf24'],
  ['#1f3a5c','#38bdf8'], ['#4a1942','#e879f9'],
]
function avatarColor(name) { return AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] }
function initials(name) { return (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) }

// ─── Birthday calculation ───────────────────────────────────────────────────────
function calcUpcomingBirthdays(people, maxDays = 30) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return (people || [])
    .filter(p => p.date_of_birth)
    .map(p => {
      const dob  = new Date(p.date_of_birth)
      let bday   = new Date(today.getFullYear(), dob.getMonth(), dob.getDate())
      if (bday < today) bday = new Date(today.getFullYear() + 1, dob.getMonth(), dob.getDate())
      const days = Math.ceil((bday - today) / (1000 * 60 * 60 * 24))
      return {
        ...p,
        daysUntilBirthday: days,
        birthdayDate: bday.toLocaleDateString('en-IN', { day: 'numeric', month: 'long' }),
        bdayYear: bday.getFullYear(),
        turningAge: bday.getFullYear() - dob.getFullYear(),
      }
    })
    .filter(p => p.daysUntilBirthday <= maxDays)
    .sort((a, b) => a.daysUntilBirthday - b.daysUntilBirthday)
}

// ─── Per-channel action buttons ─────────────────────────────────────────────────
// id + keyDate make localStorage keys unique per reminder per day/year
// Each channel (WA / Email / SMS) tracks independently — a ✓ tick appears after use
// "Mark as Reminded ✓" hides the buttons and shows a badge; Undo reverses it
function ActionButtons({ id, keyDate, phone, email, onWhatsApp, onEmail, onSMS }) {
  const k      = ch => `reminder_${ch}_${id}_${keyDate}`
  const ls     = key => localStorage.getItem(key) === 'true'
  const [waDone,    setWaDone]    = useState(() => ls(k('wa')))
  const [emailDone, setEmailDone] = useState(() => ls(k('email')))
  const [smsDone,   setSmsDone]   = useState(() => ls(k('sms')))
  const [done,      setDone]      = useState(() => ls(k('done')))

  function track(ch, setter, fn) {
    fn?.()
    localStorage.setItem(k(ch), 'true')
    setter(true)
  }
  function markDone()  { localStorage.setItem(k('done'), 'true');  setDone(true)  }
  function undoDone()  { localStorage.removeItem(k('done'));        setDone(false) }

  const pill = (bg, color, border) => ({
    height: 28, padding: '0 11px', borderRadius: 20,
    border: `1.5px solid ${border}`, background: bg, color,
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
    fontFamily: T.font, display: 'inline-flex', alignItems: 'center', gap: 4,
    whiteSpace: 'nowrap',
  })
  const disabled = {
    ...pill(T.gray100, T.gray400, T.gray200),
    cursor: 'not-allowed', opacity: 0.55,
  }

  if (done) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...pill(T.green50, T.green600, T.green100), cursor: 'default' }}>
          ✓ Reminded
        </span>
        <button onClick={undoDone} style={{
          background: 'none', border: 'none', color: T.gray400,
          fontSize: 12, cursor: 'pointer', fontFamily: T.font,
          textDecoration: 'underline', padding: 0,
        }}>Undo</button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* WhatsApp */}
      <button
        onClick={() => track('wa', setWaDone, onWhatsApp)}
        disabled={!phone}
        style={phone ? pill('#dcfce7', '#15803d', '#86efac') : disabled}
      >{waDone ? '✓ ' : ''}📱 WhatsApp</button>

      {/* Email */}
      <button
        onClick={() => track('email', setEmailDone, onEmail)}
        disabled={!email}
        style={email ? pill(T.blue50, T.blue600, T.blue100) : disabled}
      >{emailDone ? '✓ ' : ''}📧 Email</button>

      {/* SMS */}
      <button
        onClick={() => track('sms', setSmsDone, onSMS)}
        disabled={!phone}
        style={phone ? pill(T.gray100, T.gray600, T.gray300) : disabled}
      >{smsDone ? '✓ ' : ''}💬 SMS</button>

      {/* Divider */}
      <span style={{ color: T.gray300, fontSize: 14, userSelect: 'none' }}>|</span>

      {/* Mark as Reminded */}
      <button onClick={markDone} style={pill(T.teal50, T.teal700, T.teal100)}>
        Mark as Reminded ✓
      </button>
    </div>
  )
}

// ─── Section header ─────────────────────────────────────────────────────────────
function SectionHeader({ icon, title, subtitle, count, onRefresh, accentColor = T.teal500 }) {
  return (
    <div style={{
      background: T.white, borderRadius: 14, padding: '18px 22px',
      marginBottom: 12, border: `1px solid ${T.gray200}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: T.gray900, display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon} {title}
          {count > 0 && (
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
              background: accentColor + '22', color: accentColor,
            }}>{count}</span>
          )}
        </h2>
        {subtitle && <div style={{ fontSize: 12.5, color: T.gray500, marginTop: 3 }}>{subtitle}</div>}
      </div>
      {onRefresh && (
        <button onClick={onRefresh} style={{
          padding: '6px 13px', borderRadius: 8, border: `1.5px solid ${T.gray200}`,
          background: T.white, color: T.gray500, fontSize: 12, cursor: 'pointer', fontFamily: T.font,
        }}>↺ Refresh</button>
      )}
    </div>
  )
}

// ─── Empty state ────────────────────────────────────────────────────────────────
function EmptyState({ emoji, title, subtitle }) {
  return (
    <div style={{
      background: T.white, borderRadius: 12, padding: '36px 24px',
      textAlign: 'center', border: `1px solid ${T.gray200}`, marginBottom: 4,
    }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{emoji}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.gray700, marginBottom: 4 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12.5, color: T.gray400 }}>{subtitle}</div>}
    </div>
  )
}

// ─── Appointment reminder card ──────────────────────────────────────────────────
function ReminderCard({ appt, msg, isMarkedDone, isEditing, editDraft, onDraftChange,
  onStartEdit, onSaveEdit, onCancelEdit, onToggleDone,
  onWhatsApp, onEmail, onSMS }) {
  const name    = appt.patients?.full_name || 'Unknown Patient'
  const [bg]    = avatarColor(name)
  const noPhone = !appt.patients?.phone

  return (
    <div style={{
      background: T.white, borderRadius: 14, padding: '16px 18px',
      border: `1.5px solid ${isMarkedDone ? T.teal100 : T.gray200}`,
      borderLeft: `4px solid ${isMarkedDone ? T.teal500 : T.amber500}`,
      opacity: isMarkedDone ? 0.72 : 1,
      transition: 'opacity .2s, border-color .2s',
      fontFamily: T.font,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${bg}, ${bg}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 800, color: T.white,
        }}>{initials(name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: T.gray900, fontSize: 14.5 }}>{name}</div>
          <div style={{ fontSize: 12.5, color: T.teal600, marginTop: 2, fontWeight: 600 }}>
            {fmtTime(appt.time)} · {appt.service_type}
          </div>
          {appt.doctor && <div style={{ fontSize: 12, color: T.gray400, marginTop: 1 }}>Dr. {appt.doctor}</div>}
          {noPhone && <div style={{ fontSize: 11.5, color: T.red600, marginTop: 3, fontWeight: 600 }}>⚠️ No phone number</div>}
        </div>
        <button onClick={onToggleDone} style={{
          padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
          background: isMarkedDone ? T.green100 : T.amber100,
          color: isMarkedDone ? T.green600 : T.amber600,
          fontSize: 12, fontWeight: 700, fontFamily: T.font, whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {isMarkedDone ? '✓ Done' : '⏳ Pending'}
        </button>
      </div>

      {/* Message */}
      {isEditing ? (
        <div style={{ marginBottom: 10 }}>
          <textarea value={editDraft} onChange={e => onDraftChange(e.target.value)} rows={5}
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              border: `1.5px solid ${T.teal400}`, borderRadius: 8, fontSize: 13,
              lineHeight: 1.65, resize: 'vertical', fontFamily: T.font, color: T.gray700,
              outline: 'none', background: T.white,
            }} />
          <div style={{ display: 'flex', gap: 7, marginTop: 7 }}>
            <button onClick={onSaveEdit} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: T.teal600, color: T.white, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>Save</button>
            <button onClick={onCancelEdit} style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 12.5, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{
          background: T.gray50, borderRadius: 9, padding: '10px 13px', fontSize: 13,
          color: T.gray600, lineHeight: 1.65, border: `1px solid ${T.gray100}`, marginBottom: 10,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{msg}</div>
      )}

      {/* Per-channel action buttons */}
      {!isEditing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <ActionButtons
            id={appt.id}
            keyDate={todayISO()}
            phone={(appt.patients?.phone || '').replace(/\D/g, '')}
            email={(appt.patients?.email || '').trim()}
            onWhatsApp={onWhatsApp}
            onEmail={onEmail}
            onSMS={onSMS}
          />
          <button onClick={onStartEdit} style={{
            height: 28, padding: '0 11px', borderRadius: 20,
            border: `1.5px solid ${T.gray200}`, background: T.white,
            color: T.gray600, fontSize: 12, cursor: 'pointer', fontFamily: T.font,
          }}>✏️ Edit msg</button>
        </div>
      )}
    </div>
  )
}

// ─── Follow-up card (compact) ───────────────────────────────────────────────────
function FollowUpCard({ rx, clinic, showDiagnosis }) {
  const cName = clinic?.name || 'the clinic'
  const name  = rx.patients?.full_name || rx.patient_name || 'Unknown Patient'
  const [bg]  = avatarColor(name)
  const phone = (rx.patients?.phone || '').replace(/\D/g, '')
  const email = (rx.patients?.email || '').trim()

  const today = new Date(); today.setHours(0, 0, 0, 0)
  const target = new Date(rx.followup_date + 'T00:00:00')
  const days = Math.ceil((target - today) / (1000 * 60 * 60 * 24))
  const isOverdue = days < 0
  const isToday   = days === 0
  const dayLabel  = isOverdue ? `${Math.abs(days)}d overdue` : isToday ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`
  const dayColor  = isOverdue ? T.red600 : isToday ? T.amber600 : days <= 3 ? '#d97706' : T.green600
  const accent    = isOverdue ? T.red600 : isToday ? T.amber500 : T.teal500

  function sendWhatsApp() {
    if (!phone) { alert('No phone number on record.'); return }
    const msg = `Hello ${name}, this is a reminder from ${cName}. Your follow-up appointment is scheduled for ${fmtDateLong(rx.followup_date)}. ${clinic?.phone ? `Please call us at ${clinic.phone} to confirm.` : 'Please call us to confirm.'} Thank you! 🌿`
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank')
  }

  function sendEmail() {
    if (!email) { alert('No email on record for this patient.'); return }
    const subject = `Follow-up Reminder - ${cName}`
    const body = `Hello ${name},\n\nThis is a reminder from ${cName}.\n\nYour follow-up appointment is scheduled for ${fmtDateLong(rx.followup_date)}.\n\nPlease call us${clinic?.phone ? ` at ${clinic.phone}` : ''} to confirm your appointment.\n\nThank you!\n${cName} 🌿`
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  function sendSMS() {
    if (!phone) { alert('No phone number on record.'); return }
    const msg = `${cName}: Dear ${name}, your follow-up is on ${fmtDateLong(rx.followup_date)}. Call us${clinic?.phone ? ` at ${clinic.phone}` : ''} to confirm.`
    window.open(`sms:+91${phone}?body=${encodeURIComponent(msg)}`, '_blank')
  }

  return (
    <div style={{
      background: T.white, borderRadius: 12, padding: '14px 18px',
      border: `1.5px solid ${isOverdue ? '#fecdd3' : isToday ? T.amber100 : T.gray200}`,
      borderLeft: `4px solid ${accent}`, fontFamily: T.font,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${bg}, ${bg}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, color: T.white,
        }}>{initials(name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: T.gray900, fontSize: 13.5 }}>{name}</div>
          {showDiagnosis && rx.diagnosis && <div style={{ fontSize: 12, color: T.gray500, marginTop: 1 }}>{rx.diagnosis}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.gray700 }}>{fmtDateShort(rx.followup_date)}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: dayColor, marginTop: 2 }}>{dayLabel}</div>
        </div>
      </div>
      <ActionButtons
        id={rx.id}
        keyDate={todayISO()}
        phone={phone}
        email={email}
        onWhatsApp={sendWhatsApp}
        onEmail={sendEmail}
        onSMS={sendSMS}
      />
    </div>
  )
}

// ─── Birthday card ──────────────────────────────────────────────────────────────
function BirthdayCard({ person, isStaff, clinic }) {
  const cName = clinic?.name || 'the clinic'
  const name  = person.full_name || person.name || 'Unknown'
  const [bg]  = avatarColor(name)
  const phone = (person.phone || '').replace(/\D/g, '')
  const email = (person.email || '').trim()
  const { daysUntilBirthday, birthdayDate, turningAge, bdayYear } = person

  const isToday    = daysUntilBirthday === 0
  const isTomorrow = daysUntilBirthday === 1
  const dayLabel   = isToday ? 'Today! 🎉' : isTomorrow ? 'Tomorrow' : `In ${daysUntilBirthday} days`
  const accent     = isToday ? T.pink500 : isTomorrow ? T.amber500 : T.purple600
  const dayColor   = isToday ? T.pink600 : isTomorrow ? T.amber600 : T.purple600
  const bgTint     = isToday ? T.pink50  : isTomorrow ? T.amber50  : T.purple50
  const borderTint = isToday ? T.pink100 : isTomorrow ? T.amber100 : T.purple100

  function sendWhatsApp() {
    if (!phone) { alert('No phone number on record.'); return }
    const msg = isStaff
      ? `Happy Birthday ${name}! 🎂 Wishing you a wonderful day filled with joy! From your ${cName} family! 🌿`
      : `Happy Birthday ${name}! 🎂🌿 Wishing you great health and happiness! As a valued patient of ${cName}, we'd love to offer you a special birthday discount on your next visit.${clinic?.phone ? ` Call us at ${clinic.phone} to know more!` : ''}`
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, '_blank')
  }

  function sendEmail() {
    if (!email) { alert('No email on record.'); return }
    const subject = `Happy Birthday ${name}! 🎂`
    const body = isStaff
      ? `Happy Birthday ${name}!\n\nWishing you a wonderful birthday filled with joy and happiness!\n\nWith love,\n${cName} Family 🌿`
      : `Dear ${name},\n\nHappy Birthday! 🎂🌿\n\nWishing you great health and happiness on your special day!\n\nAs a valued patient of ${cName}, we'd love to offer you a special birthday discount on your next visit.\n\nCall us${clinic?.phone ? ` at ${clinic.phone}` : ''} to know more!\n\nWarm regards,\nTeam ${cName}`
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  function sendSMS() {
    if (!phone) { alert('No phone number on record.'); return }
    const msg = `Happy Birthday ${name}! 🎂 Warm wishes from ${cName}! 🌿`
    window.open(`sms:+91${phone}?body=${encodeURIComponent(msg)}`, '_blank')
  }

  return (
    <div style={{
      background: bgTint, borderRadius: 12, padding: '14px 18px',
      border: `1.5px solid ${borderTint}`, borderLeft: `4px solid ${accent}`,
      fontFamily: T.font,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${bg}, ${bg}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, color: T.white,
        }}>{initials(name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: T.gray900, fontSize: 13.5 }}>{name}</div>
          <div style={{ fontSize: 12, color: T.gray500, marginTop: 1 }}>
            {birthdayDate}{turningAge ? ` · Turning ${turningAge}` : ''}
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 800, color: dayColor }}>{dayLabel}</div>
      </div>
      <ActionButtons
        id={person.id}
        keyDate={String(bdayYear || new Date().getFullYear())}
        phone={phone}
        email={email}
        onWhatsApp={sendWhatsApp}
        onEmail={sendEmail}
        onSMS={sendSMS}
      />
    </div>
  )
}

// ─── Settings panel ────────────────────────────────────────────────────────────
function SettingsPanel({ templates, clinic, onSave, onClose }) {
  const [draft, setDraft] = useState(templates)
  const inp = {
    width: '100%', padding: '9px 11px', border: `1.5px solid ${T.gray200}`,
    borderRadius: 8, fontSize: 13, fontFamily: T.font, color: T.gray800,
    outline: 'none', boxSizing: 'border-box', background: T.gray50,
  }
  const lbl = { fontSize: 12, fontWeight: 600, color: T.gray600, display: 'block', marginBottom: 5 }
  const TMPL_FIELDS = [
    { key: 'consultation', label: 'Consultation Template' },
    { key: 'followup',     label: 'Follow-up Template' },
    { key: 'treatment',    label: 'Treatment / Procedure Template' },
  ]
  return (
    <div style={{
      background: T.white, borderRadius: 14, border: `1.5px solid ${T.gray200}`,
      padding: '20px 22px', marginTop: 24, fontFamily: T.font,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${T.gray100}`,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.gray900 }}>⚙️ Reminder Settings</div>
          <div style={{ fontSize: 12, color: T.gray400, marginTop: 2 }}>
            Use <code style={{ background: T.gray100, padding: '1px 5px', borderRadius: 4 }}>{'{Name}'}</code>,{' '}
            <code style={{ background: T.gray100, padding: '1px 5px', borderRadius: 4 }}>{'{Time}'}</code>,{' '}
            <code style={{ background: T.gray100, padding: '1px 5px', borderRadius: 4 }}>{'{Service}'}</code>,{' '}
            <code style={{ background: T.gray100, padding: '1px 5px', borderRadius: 4 }}>{'{Clinic}'}</code>,{' '}
            <code style={{ background: T.gray100, padding: '1px 5px', borderRadius: 4 }}>{'{ClinicPhone}'}</code> as placeholders
          </div>
        </div>
        <button onClick={onClose} style={{
          background: T.gray100, border: 'none', borderRadius: 7, width: 30, height: 30,
          cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>×</button>
      </div>
      <div style={{ marginBottom: 16, fontSize: 12.5, color: T.gray500 }}>
        Clinic name &amp; phone used in messages: <strong style={{ color: T.gray700 }}>{[clinic?.name, clinic?.phone].filter(Boolean).join(' · ') || '—'}</strong>
        {' '}(edit in Settings → Clinic Information)
      </div>
      {TMPL_FIELDS.map(({ key, label }) => (
        <div key={key} style={{ marginBottom: 16 }}>
          <label style={lbl}>{label}</label>
          <textarea value={draft[key]} rows={4}
            onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
            style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={() => onSave(draft)} style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: T.teal700, color: T.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>💾 Save Settings</button>
        <button onClick={() => setDraft(DEFAULT_TEMPLATES)} style={{ padding: '9px 16px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray600, fontSize: 13, cursor: 'pointer', fontFamily: T.font }}>↺ Reset to Defaults</button>
        <button onClick={onClose} style={{ marginLeft: 'auto', padding: '9px 16px', borderRadius: 8, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray500, fontSize: 13, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
      </div>
    </div>
  )
}

// ─── Main Reminders component ──────────────────────────────────────────────────
export default function Reminders() {
  const { clinic, can } = useAuth()
  // Follow-ups are read from prescriptions — admin/doctor only (RLS)
  const clinical = can('clinical.view')
  const tomorrow = tomorrowISO()

  // ── Appointments state ──
  const [appts, setAppts]               = useState([])
  const [loading, setLoading]           = useState(true)
  const [statusMap, setStatusMap]       = useState({})   // { [id]: 'pending'|'done' }
  const [msgMap, setMsgMap]             = useState({})
  const [editing, setEditing]           = useState(null)
  const [editDraft, setEditDraft]       = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [templates, setTemplates]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('md_reminder_templates') || 'null') || DEFAULT_TEMPLATES }
    catch { return DEFAULT_TEMPLATES }
  })

  // ── Follow-ups state ──
  const [followUps, setFollowUps]                   = useState([])
  const [followUpsLoading, setFollowUpsLoading]     = useState(true)

  // ── Birthdays state ──
  const [patientBdays, setPatientBdays]             = useState([])
  const [patientBdaysLoading, setPatientBdaysLoading] = useState(true)
  const [staffBdays, setStaffBdays]                 = useState([])
  const [staffBdaysLoading, setStaffBdaysLoading]   = useState(true)

  // ── Load appointment done-status from localStorage ──
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('md_reminder_status_' + tomorrow) || '{}')
      setStatusMap(saved)
    } catch {}
  }, [tomorrow])

  // ── Fetch tomorrow's appointments ──
  const load = useCallback(async () => {
    setLoading(true)
    const d = new Date()
    d.setDate(d.getDate() + 1)
    const tomorrowStr = localISO(d)

    const { data } = await supabase
      .from('appointments')
      .select('*, patients(full_name, phone, email)')
      .eq('date', tomorrowStr)
      .order('time', { ascending: true })

    const tomorrowAppts = (data || []).filter(a => !['Cancelled', 'Completed', 'No Show'].includes(a.status))
    setAppts(tomorrowAppts)
    setLoading(false)
  }, [])

  // ── Fetch upcoming follow-ups ──
  const loadFollowUps = useCallback(async () => {
    if (!clinical) { setFollowUps([]); setFollowUpsLoading(false); return }
    setFollowUpsLoading(true)
    const today = todayISO()
    const { data } = await supabase
      .from('prescriptions')
      .select('id, patient_id, patient_name, followup_date, follow_up_date, follow_up, diagnosis, patients(full_name, phone, email)')
      .or(`followup_date.gte.${today},follow_up_date.gte.${today}`)
      .order('followup_date', { ascending: true })
      .limit(100)

    const normalised = (data || []).map(r => ({
      ...r, followup_date: r.followup_date || r.follow_up_date,
    })).filter(r => r.followup_date)

    // Deduplicate — keep one per patient
    const seen = new Set()
    const unique = normalised.filter(r => {
      if (seen.has(r.patient_id)) return false
      seen.add(r.patient_id)
      return true
    })
    setFollowUps(unique)
    setFollowUpsLoading(false)
  }, [clinical])

  // ── Fetch patient birthdays ──
  const loadPatientBdays = useCallback(async () => {
    setPatientBdaysLoading(true)
    const { data: patients } = await supabase.from('patients').select('id, full_name, phone, email, date_of_birth')
    const patientsWithDOB = (patients || []).filter(p => p.date_of_birth)
    setPatientBdays(calcUpcomingBirthdays(patientsWithDOB, 30))
    setPatientBdaysLoading(false)
  }, [])

  // ── Fetch staff birthdays ──
  const loadStaffBdays = useCallback(async () => {
    setStaffBdaysLoading(true)
    const { data } = await supabase.from('staff').select('id, full_name, phone, email, date_of_birth').not('date_of_birth', 'is', null)
    setStaffBdays(calcUpcomingBirthdays(data || [], 30))
    setStaffBdaysLoading(false)
  }, [])

  useEffect(() => { load() },             [load])
  useEffect(() => { loadFollowUps() },    [loadFollowUps])
  useEffect(() => { loadPatientBdays() }, [loadPatientBdays])
  useEffect(() => { loadStaffBdays() },   [loadStaffBdays])

  // ── Appointment helpers ──
  function getMsg(appt) { return msgMap[appt.id] || buildMessage(appt, templates, clinic) }

  function toggleDone(id) {
    setStatusMap(prev => {
      const next = { ...prev, [id]: prev[id] === 'done' ? 'pending' : 'done' }
      localStorage.setItem('md_reminder_status_' + tomorrow, JSON.stringify(next))
      return next
    })
  }

  function sendApptWhatsApp(appt) {
    const num = (appt.patients?.phone || '').replace(/\D/g, '')
    if (!num) return
    window.open(`https://wa.me/91${num}?text=${encodeURIComponent(getMsg(appt))}`, '_blank')
    // Note: no auto-mark-done — user must click "Mark as Reminded ✓" or toggle
  }

  function sendApptEmail(appt) {
    const email = (appt.patients?.email || '').trim()
    if (!email) { alert('No email on file for this patient.'); return }
    const subject = `Appointment Reminder - ${clinic?.name || 'Clinic'}`
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(getMsg(appt))}`
  }

  function sendApptSMS(appt) {
    const num = (appt.patients?.phone || '').replace(/\D/g, '')
    if (!num) { alert('No phone on file for this patient.'); return }
    window.open(`sms:+91${num}?body=${encodeURIComponent(getMsg(appt))}`, '_blank')
  }

  function sendAll() {
    const pending = appts.filter(a => statusMap[a.id] !== 'done' && a.patients?.phone)
    if (!pending.length) return
    pending.forEach((a, i) => setTimeout(() => sendApptWhatsApp(a), i * 700))
  }

  function startEdit(appt) { setEditing(appt.id); setEditDraft(getMsg(appt)) }
  function saveEdit(id)    { setMsgMap(prev => ({ ...prev, [id]: editDraft })); setEditing(null) }

  function saveSettings(newTemplates) {
    setTemplates(newTemplates)
    localStorage.setItem('md_reminder_templates', JSON.stringify(newTemplates))
    setMsgMap({})
    setShowSettings(false)
  }

  const doneCount    = appts.filter(a => statusMap[a.id] === 'done').length
  const pendingCount = appts.length - doneCount
  const pendingWithPhone = appts.filter(a => statusMap[a.id] !== 'done' && a.patients?.phone).length

  return (
    <div style={{
      height: '100%', overflowY: 'auto', background: T.gray100,
      padding: '24px 28px', fontFamily: T.font,
    }}>

      {/* ══ SECTION 1 — Tomorrow's Appointments ══ */}
      <div style={{
        background: T.white, borderRadius: 14, padding: '20px 24px',
        marginBottom: 20, border: `1px solid ${T.gray200}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.gray900, display: 'flex', alignItems: 'center', gap: 10 }}>
              🔔 Tomorrow's Appointments
            </h2>
            <div style={{ fontSize: 13, color: T.gray500, marginTop: 4 }}>{fmtDateLong(tomorrow)}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ padding: '7px 16px', borderRadius: 24, background: T.green50, border: `1.5px solid ${T.green100}`, color: T.green600, fontSize: 13, fontWeight: 700 }}>✓ {doneCount} Done</div>
            <div style={{ padding: '7px 16px', borderRadius: 24, background: T.amber50, border: `1.5px solid ${T.amber100}`, color: T.amber600, fontSize: 13, fontWeight: 700 }}>⏳ {pendingCount} Pending</div>
            <button onClick={() => load()} style={{ padding: '7px 13px', borderRadius: 24, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray500, fontSize: 12.5, cursor: 'pointer', fontFamily: T.font }}>↺ Refresh</button>
            <button onClick={() => setShowSettings(s => !s)} style={{ padding: '7px 14px', borderRadius: 24, border: `1.5px solid ${T.gray200}`, background: T.white, color: T.gray500, fontSize: 13, cursor: 'pointer', fontFamily: T.font }}>⚙️ Settings</button>
            {pendingWithPhone > 0 && (
              <button onClick={sendAll} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: '#25d366', color: T.white, fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: T.font, boxShadow: '0 2px 10px rgba(37,211,102,0.35)', display: 'flex', alignItems: 'center', gap: 8 }}>
                💬 Send All ({pendingWithPhone})
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: T.gray400, fontSize: 14 }}>Loading tomorrow's appointments…</div>
      ) : appts.length === 0 ? (
        <EmptyState emoji="🎉" title="No appointments tomorrow" subtitle="Nothing to remind — enjoy a calm day!" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 4 }}>
          {appts.map(appt => (
            <ReminderCard
              key={appt.id}
              appt={appt}
              msg={getMsg(appt)}
              isMarkedDone={statusMap[appt.id] === 'done'}
              isEditing={editing === appt.id}
              editDraft={editDraft}
              onDraftChange={setEditDraft}
              onStartEdit={() => startEdit(appt)}
              onSaveEdit={() => saveEdit(appt.id)}
              onCancelEdit={() => setEditing(null)}
              onToggleDone={() => toggleDone(appt.id)}
              onWhatsApp={() => sendApptWhatsApp(appt)}
              onEmail={() => sendApptEmail(appt)}
              onSMS={() => sendApptSMS(appt)}
            />
          ))}
        </div>
      )}

      {showSettings && (
        <SettingsPanel templates={templates} clinic={clinic} onSave={saveSettings} onClose={() => setShowSettings(false)} />
      )}

      {appts.length > 0 && !loading && (
        <div style={{ marginTop: 12, marginBottom: 8, padding: '10px 16px', borderRadius: 10, background: T.teal50, border: `1px solid ${T.teal100}`, fontSize: 12, color: T.teal700, textAlign: 'center' }}>
          💡 Click each channel button to send. Each button shows a ✓ tick after use. Click "Mark as Reminded ✓" when done.
        </div>
      )}

      {/* ══ SECTION 2 — Upcoming Follow-ups ══ */}
      <div style={{ marginTop: 28 }}>
        <SectionHeader icon="📅" title="Upcoming Follow-ups" subtitle="Patients with scheduled follow-up dates" count={followUps.length} onRefresh={loadFollowUps} accentColor={T.teal500} />
        {followUpsLoading ? (
          <div style={{ textAlign: 'center', padding: '36px 0', color: T.gray400, fontSize: 14 }}>Loading follow-ups…</div>
        ) : followUps.length === 0 ? (
          <EmptyState emoji="✅" title="No upcoming follow-ups" subtitle="Prescriptions with follow-up dates will appear here" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {followUps.map(rx => <FollowUpCard key={rx.id} rx={rx} clinic={clinic} showDiagnosis={clinical} />)}
          </div>
        )}
      </div>

      {/* ══ SECTION 3 — Patient Birthdays ══ */}
      <div style={{ marginTop: 28 }}>
        <SectionHeader icon="🎂" title="Patient Birthdays" subtitle="Upcoming birthdays in the next 30 days" count={patientBdays.length} onRefresh={loadPatientBdays} accentColor={T.pink500} />
        {patientBdaysLoading ? (
          <div style={{ textAlign: 'center', padding: '36px 0', color: T.gray400, fontSize: 14 }}>Loading birthdays…</div>
        ) : patientBdays.length === 0 ? (
          <EmptyState emoji="🎂" title="No upcoming patient birthdays" subtitle="Patients with date_of_birth set will appear here" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {patientBdays.map(p => <BirthdayCard key={p.id} person={p} isStaff={false} clinic={clinic} />)}
          </div>
        )}
      </div>

      {/* ══ SECTION 4 — Staff Birthdays ══ */}
      <div style={{ marginTop: 28, marginBottom: 32 }}>
        <SectionHeader icon="👥" title="Staff Birthdays" subtitle="Upcoming birthdays in the next 30 days" count={staffBdays.length} onRefresh={loadStaffBdays} accentColor={T.purple600} />
        {staffBdaysLoading ? (
          <div style={{ textAlign: 'center', padding: '36px 0', color: T.gray400, fontSize: 14 }}>Loading staff birthdays…</div>
        ) : staffBdays.length === 0 ? (
          <EmptyState emoji="👥" title="No upcoming staff birthdays" subtitle="Staff with date_of_birth set will appear here" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {staffBdays.map(s => <BirthdayCard key={s.id} person={s} isStaff={true} clinic={clinic} />)}
          </div>
        )}
      </div>

    </div>
  )
}

// ─── Exported helper for Dashboard bell badge ──────────────────────────────────
export async function fetchTomorrowPendingCount() {
  const tomorrow = tomorrowISO()
  const { count } = await supabase
    .from('appointments')
    .select('*', { count: 'exact', head: true })
    .eq('date', tomorrow)
    .not('status', 'in', '("Cancelled","Completed","No Show")')
  try {
    const saved = JSON.parse(localStorage.getItem('md_reminder_status_' + tomorrow) || '{}')
    const doneN = Object.values(saved).filter(v => v === 'done').length
    return Math.max(0, (count || 0) - doneN)
  } catch {
    return count || 0
  }
}
